// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — datasets collected into Khipus (owner, 2026-11-03: playlists
 * retire, as they did in AYNI 3D, 2D and the Museum; a dataset's menu says
 * "add to a new (or existing) Khipu"). Pure: no DOM, no network.
 *
 * - `addDatasetStep` / `removeDatasetSteps` / `hasDataset`: what the dataset
 *   menu does to a story. A dataset is added as one plain step (the dataset,
 *   moving on when Next is pressed) and taken out only where its steps are
 *   still plain, so a step someone wrote words for, placed pins on or timed
 *   is never lost from a menu.
 * - `storyWithDataset`: a new story whose one step is that dataset.
 * - `khipuLoops` / `withLoop`: "Start again after the last step", kept as
 *   the top-level extension field `x-ayni-loop` (the schema allows `x-`
 *   fields there and other players ignore them), so the synced Khipu files
 *   stay as they are.
 * - `readLegacyPlaylists` / `playlistToKhipu`: the playlists people had
 *   (a browser's `sos-playlists.v1`, an account's copy, or an exported
 *   `.json`), each read once as a story, one step per dataset: "wait for a
 *   click" becomes Next, a time becomes a timer, loop stays loop.
 */

import type { Khipu, KhipuDatasetRef, KhipuStep, KhipuText } from './khipu'
import { KHIPU_SCHEMA_VERSION } from './khipu'

/** The extension field that makes XR's player start again after the last step. */
export const KHIPU_LOOP_FIELD = 'x-ayni-loop'

/** Seconds a playlist entry showed when it named none (the playlists' default). */
export const LEGACY_ENTRY_SECONDS = 30

export function khipuLoops(k: Khipu): boolean {
  return k[KHIPU_LOOP_FIELD] === true
}

/** The Khipu with loop on or off (off removes the field). */
export function withLoop(k: Khipu, on: boolean): Khipu {
  const { [KHIPU_LOOP_FIELD]: _was, ...rest } = k
  return (on ? { ...rest, [KHIPU_LOOP_FIELD]: true } : rest) as Khipu
}

/** The app dataset id a reference names, if it names one. */
export function appIdOf(ref: KhipuDatasetRef): string | null {
  if (typeof ref.appId === 'string' && ref.appId) return ref.appId
  return ref.ref.startsWith('app:') ? ref.ref.slice(4) : null
}

/** The app dataset ids a Khipu's datasets name (the dataset menu's ticks). */
export function appDatasetIds(datasets: readonly KhipuDatasetRef[]): string[] {
  const out: string[] = []
  for (const d of datasets) {
    const id = appIdOf(d)
    if (id && !out.includes(id)) out.push(id)
  }
  return out
}

/** The keys of the Khipu's references to this dataset. */
function keysOf(k: Khipu, datasetId: string): Set<string> {
  return new Set(k.datasets.filter((d) => appIdOf(d) === datasetId).map((d) => d.key))
}

/** The keys a step shows: its dataset, its layers and its other globes. */
function stepKeys(s: KhipuStep): string[] {
  const out: string[] = []
  if (typeof s.dataset === 'string') out.push(s.dataset)
  for (const l of s.layers ?? []) out.push(l.dataset)
  for (const g of s.globes ?? []) if (typeof g.dataset === 'string') out.push(g.dataset)
  return out
}

/** Whether some step shows this dataset. */
export function hasDataset(k: Khipu, datasetId: string): boolean {
  const keys = keysOf(k, datasetId)
  return keys.size > 0 && k.path.steps.some((s) => stepKeys(s).some((key) => keys.has(key)))
}

/** The fields a plain step has: what `addDatasetStep` writes (its dataset, and Next or a timer). */
const PLAIN_FIELDS = new Set(['id', 'n', 'dataset', 'advance'])

/** Whether a step shows only this dataset, as the menu added it. */
function isPlainStepOf(s: KhipuStep, keys: Set<string>): boolean {
  return typeof s.dataset === 'string' && keys.has(s.dataset) && Object.keys(s).every((f) => PLAIN_FIELDS.has(f))
}

/** A step id no step of the path uses. */
function freshStepId(steps: readonly KhipuStep[]): string {
  const used = new Set(steps.map((s) => s.id))
  let n = steps.length + 1
  while (used.has(`s${n}`)) n++
  return `s${n}`
}

/** The reference key for this dataset, added to the Khipu's datasets when new. */
function datasetKey(datasets: KhipuDatasetRef[], datasetId: string, title: string): { datasets: KhipuDatasetRef[]; key: string } {
  const found = datasets.find((d) => appIdOf(d) === datasetId)
  if (found) return { datasets, key: found.key }
  const used = new Set(datasets.map((d) => d.key))
  let n = datasets.length + 1
  while (used.has(`d${n}`)) n++
  const key = `d${n}`
  return { datasets: [...datasets, { key, ref: `app:${datasetId}`, appId: datasetId, title: { en: title } }], key }
}

const renumber = (steps: KhipuStep[]): KhipuStep[] => steps.map((s, i) => (s.n === i + 1 ? s : { ...s, n: i + 1 }))

/**
 * The Khipu with this dataset as one more step at the end (Next moves on,
 * or a timer of `seconds`). A Khipu that already shows the dataset comes
 * back unchanged, as a playlist ignored a dataset it had.
 */
export function addDatasetStep(k: Khipu, datasetId: string, title: string, opts: { seconds?: number; force?: boolean } = {}): Khipu {
  if (!datasetId || (!opts.force && hasDataset(k, datasetId))) return k
  const { datasets, key } = datasetKey(k.datasets, datasetId, title)
  const seconds = opts.seconds
  const step: KhipuStep = {
    id: freshStepId(k.path.steps),
    n: k.path.steps.length + 1,
    dataset: key,
    advance: typeof seconds === 'number' && Number.isFinite(seconds) && seconds >= 1 ? { mode: 'timer', seconds } : { mode: 'manual' },
  }
  return { ...k, datasets, path: { ...k.path, steps: renumber([...k.path.steps, step]) } }
}

/**
 * The Khipu without this dataset's plain steps, and without the reference
 * when nothing uses it any more. Null when the menu cannot take it out:
 * no plain step shows it (it is in a step someone wrote; the editor takes
 * that out), or it would leave the Khipu with no step at all.
 */
export function removeDatasetSteps(k: Khipu, datasetId: string): Khipu | null {
  const keys = keysOf(k, datasetId)
  const steps = k.path.steps.filter((s) => !isPlainStepOf(s, keys))
  if (steps.length === k.path.steps.length || steps.length === 0) return null
  const stillUsed = new Set(steps.flatMap(stepKeys))
  const log = JSON.stringify(k.log ?? {})
  const datasets = k.datasets.filter((d) => !keys.has(d.key) || stillUsed.has(d.key) || log.includes(`"${d.key}"`))
  return { ...k, datasets, path: { ...k.path, steps: renumber(steps) } }
}

/** Whether the menu can take the dataset out of this Khipu (see `removeDatasetSteps`). */
export function canRemoveDataset(k: Khipu, datasetId: string): boolean {
  return removeDatasetSteps(k, datasetId) !== null
}

const iso = (ms: number) => new Date(ms).toISOString().replace(/\.\d{3}Z$/, 'Z')

/** A new story, as a draft, with no steps yet. */
function blankStory(id: string, title: KhipuText, lang: string, created: string, modified: string): Khipu {
  return {
    format: 'ayni-khipu',
    schemaVersion: KHIPU_SCHEMA_VERSION,
    id,
    kind: 'story',
    status: 'draft',
    title,
    languages: [lang],
    created,
    modified,
    privacy: { mode: 'anonymous' },
    datasets: [],
    path: { steps: [] },
  }
}

/** A new story named `name` whose one step is this dataset. */
export function storyWithDataset(id: string, name: string, lang: string, nowMs: number, datasetId: string, datasetTitle: string): Khipu {
  const at = iso(nowMs)
  return addDatasetStep(blankStory(id, { [lang]: name }, lang, at, at), datasetId, datasetTitle)
}

// ------------------------------------------------------------ old playlists

/** One playlist as AYNI XR kept them until 2026-11-03 (`sos-playlists.v1`). */
export interface LegacyPlaylist {
  id: string
  name: string
  createdAt?: string
  updatedAt?: string
  loop?: boolean
  datasets: Array<{ datasetId: string; durationSec?: number; pauseForInput?: boolean }>
}

/**
 * The playlists in what was stored or exported: an array of playlists, or
 * an account's `{ playlists, deleted }`, the deleted ones left out. Anything
 * else reads as none; a malformed entry is skipped.
 */
export function readLegacyPlaylists(raw: unknown): LegacyPlaylist[] {
  const list = Array.isArray(raw) ? raw : raw && typeof raw === 'object' && Array.isArray((raw as { playlists?: unknown }).playlists) ? (raw as { playlists: unknown[] }).playlists : []
  const deleted = new Set(
    raw && typeof raw === 'object' && Array.isArray((raw as { deleted?: unknown }).deleted)
      ? ((raw as { deleted: unknown[] }).deleted).map((d) => (d && typeof d === 'object' ? (d as { id?: unknown }).id : null)).filter((id): id is string => typeof id === 'string')
      : [],
  )
  const out: LegacyPlaylist[] = []
  for (const item of list) {
    if (!item || typeof item !== 'object') continue
    const p = item as Record<string, unknown>
    if (typeof p.id !== 'string' || !p.id || deleted.has(p.id) || typeof p.name !== 'string' || !Array.isArray(p.datasets)) continue
    const datasets: LegacyPlaylist['datasets'] = []
    for (const e of p.datasets) {
      if (!e || typeof e !== 'object') continue
      const entry = e as Record<string, unknown>
      if (typeof entry.datasetId !== 'string' || !entry.datasetId) continue
      datasets.push({
        datasetId: entry.datasetId,
        ...(typeof entry.durationSec === 'number' && Number.isFinite(entry.durationSec) && entry.durationSec > 0 ? { durationSec: entry.durationSec } : {}),
        ...(entry.pauseForInput === true ? { pauseForInput: true } : {}),
      })
    }
    out.push({
      id: p.id,
      name: p.name.trim().slice(0, 120),
      ...(typeof p.createdAt === 'string' ? { createdAt: p.createdAt } : {}),
      ...(typeof p.updatedAt === 'string' ? { updatedAt: p.updatedAt } : {}),
      ...(p.loop === true ? { loop: true } : {}),
      datasets,
    })
  }
  return out
}

/** The newer of two copies of the same playlist (a browser's and an account's). */
export function newerPlaylist(a: LegacyPlaylist, b: LegacyPlaylist): LegacyPlaylist {
  return (b.updatedAt ?? b.createdAt ?? '') > (a.updatedAt ?? a.createdAt ?? '') ? b : a
}

/** FNV-1a, 64 bit, over a text's UTF-16 code units, as 16 hex digits (the same as AYNI 3D's `khipuIdForPlaylist`). */
export function fnv1a64(text: string): string {
  let h = 0xcbf29ce484222325n
  for (let i = 0; i < text.length; i++) {
    const unit = text.charCodeAt(i)
    for (const byte of [unit & 0xff, unit >> 8]) {
      h ^= BigInt(byte)
      h = (h * 0x100000001b3n) & 0xffffffffffffffffn
    }
  }
  return h.toString(16).padStart(16, '0')
}

/** A stable Khipu id for a playlist id, so the same playlist read on two devices is one Khipu, not two. */
export function khipuIdForPlaylist(playlistId: string): string {
  return fnv1a64(playlistId)
}

/**
 * A playlist as a story: one step per dataset ("wait for a click" → Next,
 * else a timer of its seconds, 30 when it named none), loop kept. A playlist
 * with no datasets keeps its name over one plain Earth step.
 */
export function playlistToKhipu(p: LegacyPlaylist, opts: { lang: string; nowMs: number; titleOf: (datasetId: string) => string | null; untitled: string }): Khipu {
  const created = p.createdAt && !Number.isNaN(Date.parse(p.createdAt)) ? iso(Date.parse(p.createdAt)) : iso(opts.nowMs)
  let k = blankStory(khipuIdForPlaylist(p.id), { [opts.lang]: p.name || opts.untitled }, opts.lang, created, iso(opts.nowMs))
  for (const e of p.datasets) {
    k = addDatasetStep(k, e.datasetId, opts.titleOf(e.datasetId) ?? e.datasetId, {
      force: true,
      ...(e.pauseForInput ? {} : { seconds: Math.max(1, Math.round(e.durationSec ?? LEGACY_ENTRY_SECONDS)) }),
    })
  }
  if (k.path.steps.length === 0) k = { ...k, path: { steps: [{ id: 's1', n: 1, dataset: null, advance: { mode: 'manual' } }] } }
  return withLoop(k, p.loop === true)
}
